import { useEffect, useState } from "react";
import { getFunnelStats, type CurrencyVolume, type FunnelStats } from "@/lib/api";
import { formatVolumes } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { TrendingUp, CheckCircle2, BarChart3 } from "lucide-react";

type Row = { status: string; count: number; volumes: CurrencyVolume[]; pct: number };

export function FunnelPage() {
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getFunnelStats({ days })
      .then(setStats)
      .finally(() => setLoading(false));
  }, [days]);

  const statuses = stats ? Object.entries(stats.by_status) : [];
  const totalCount = statuses.reduce((sum, [, v]) => sum + v.count, 0);
  const succeededCount = stats?.by_status.succeeded?.count ?? 0;
  const conversionRate = totalCount > 0 ? ((succeededCount / totalCount) * 100).toFixed(1) : "0";

  const rows: Row[] = statuses.map(([status, v]) => ({
    status,
    count: v.count,
    volumes: v.volumes,
    pct: totalCount > 0 ? (v.count / totalCount) * 100 : 0,
  }));

  const columns: ColumnDef<Row>[] = [
    {
      key: "status",
      header: "Statut",
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "count",
      header: "Nb transactions",
      align: "right",
      sortable: true,
      accessor: (r) => r.count,
      cell: (r) => <span className="font-semibold text-[#181c32]">{r.count.toLocaleString("fr-FR")}</span>,
    },
    {
      key: "volume",
      header: "Volume",
      align: "right",
      sortable: false,
      accessor: (r) => r.volumes.reduce((sum, v) => sum + v.amount_minor, 0),
      cell: (r) => <span>{formatVolumes(r.volumes)}</span>,
    },
    {
      key: "pct",
      header: "% du total",
      align: "right",
      sortable: true,
      accessor: (r) => r.pct,
      cell: (r) => (
        <div className="inline-flex items-center gap-2 justify-end w-full">
          <div className="w-20 h-1.5 rounded-full bg-[#eff2f5] overflow-hidden">
            <div className="h-full bg-brand-500" style={{ width: `${r.pct}%` }} />
          </div>
          <span className="text-sm font-semibold w-12 text-right">{r.pct.toFixed(1)}%</span>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Funnel & stats"
        breadcrumbs={[{ label: "Plateforme" }, { label: "Funnel" }]}
        actions={
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="form-input py-2 px-3 text-sm w-auto">
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
        }
      />

      {loading && <div className="card card-body text-[#7e8299]">Chargement…</div>}
      {stats && !loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
            <KPI label="Total tentatives" value={String(totalCount)} icon={<BarChart3 size={20} />} color="primary" />
            <KPI label="Paiements réussis" value={String(succeededCount)} icon={<CheckCircle2 size={20} />} color="success" />
            <KPI label="Taux de conversion" value={`${conversionRate}%`} icon={<TrendingUp size={20} />} color="info" />
          </div>

          <DataTable<Row>
            title="Décomposition par statut"
            subtitle={`Période: ${days} jours glissants`}
            columns={columns}
            data={rows}
            rowKey={(r) => r.status}
            initialSort={{ key: "count", dir: "desc" }}
          />
        </>
      )}
    </div>
  );
}

function KPI({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "info";
}) {
  const colorMap = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    info: "bg-info-50 text-info-700",
  };
  return (
    <div className="card card-body flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorMap[color]}`}>{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
        <div className="text-3xl font-bold text-[#181c32] mt-1">{value}</div>
      </div>
    </div>
  );
}
